import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Heart, Trash2 } from 'lucide-react'
import { useWishlist } from '../../context/wishlist-context'
import { useAuth } from '../../context/auth-context'
import { getWishlist } from '../../services/wishlistApi'
import { WishlistItem } from '../../types/wishlist'
import { useWishlistProducts } from '../../hooks/useWishlistProducts'
import { API_BASE_URL } from 'config/api'
import Price from 'ui/price'
import { makeNumbersFarsi } from 'utils/farsi'
import { FullPageSpinner } from 'ui/FullPageSpinner'
import { getBestImageUrl, getImageAlt } from 'utils/imageUtils'
import { slugify } from 'utils/slugify'

export const WishlistPage: React.FC = () => {
  const { user } = useAuth()
  const { wishlist, removeItem, loading: contextLoading } = useWishlist()
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([])
  const [loading, setLoading] = useState(true)
  const isAuthenticated = !!user

  // For guest users, fetch product details from API
  const {
    products: guestProducts,
    loading: guestLoading,
    error: guestError,
  } = useWishlistProducts(isAuthenticated ? [] : wishlist)

  useEffect(() => {
    loadWishlistData()
  }, [isAuthenticated, wishlist])

  const loadWishlistData = async () => {
    setLoading(true)
    try {
      if (isAuthenticated) {
        // Fetch full wishlist with product details from API
        const response = await getWishlist()
        if (response.success) {
          setWishlistItems(response.data.items)
        }
      }
      // For guest users, we'll use the guestProducts from the hook
    } catch (error) {
      console.error('Failed to load wishlist:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRemove = async (productId: string) => {
    const success = await removeItem(productId)
    if (success) {
      // Reload wishlist data after removal
      await loadWishlistData()
    }
  }

  // Determine if we're still loading
  const isLoading = loading || contextLoading || (!isAuthenticated && guestLoading)

  if (isLoading) {
    return <FullPageSpinner />
  }

  return (
    <div
      className='min-h-screen py-8'
      style={{ backgroundColor: 'var(--bg-secondary)' }}
    >
      <Helmet>
        <title>علاقه‌مندی‌ها - بوک استوری</title>
        <meta
          name='description'
          content='لیست کتاب‌های مورد علاقه شما در بوک استوری'
        />
      </Helmet>

      <div className='container mx-auto px-4'>
        <div className='flex items-center gap-3 mb-8'>
          <Heart
            size={32}
            fill='var(--accent-primary)'
            color='var(--accent-primary)'
          />
          <h1
            className='text-3xl font-bold'
            style={{ color: 'var(--text-primary)' }}
          >
            علاقه‌مندی‌های من
          </h1>
        </div>

        {!isAuthenticated && wishlist.length > 0 && (
          <div
            className='mb-6 p-4 rounded-lg border'
            style={{
              backgroundColor: 'var(--card-bg)',
              borderColor: 'var(--border-color)',
            }}
          >
            <p
              className='text-base'
              style={{ color: 'var(--text-secondary)' }}
            >
              شما {makeNumbersFarsi(wishlist.length)} محصول در لیست علاقه‌مندی‌های
              خود دارید.{' '}
              <Link
                to='/login'
                className='font-semibold underline'
                style={{ color: 'var(--accent-primary)' }}
              >
                وارد شوید
              </Link>{' '}
              تا لیست شما همیشه در دسترس باشد.
            </p>
          </div>
        )}

        {wishlist.length === 0 ? (
          <div
            className='text-center py-16 rounded-lg'
            style={{ backgroundColor: 'var(--card-bg)' }}
          >
            <Heart
              size={64}
              className='mx-auto mb-4'
              style={{ color: 'var(--text-secondary)' }}
            />
            <h2
              className='text-2xl font-semibold mb-2'
              style={{ color: 'var(--text-primary)' }}
            >
              لیست علاقه‌مندی‌های شما خالی است
            </h2>
            <p className='text-lg mb-6' style={{ color: 'var(--text-secondary)' }}>
              محصولات مورد علاقه خود را به لیست اضافه کنید
            </p>
            <Link
              to='/'
              className='inline-block px-6 py-3 rounded-lg text-white font-semibold transition-all duration-300 transform hover:scale-105'
              style={{ backgroundColor: 'var(--accent-primary)' }}
            >
              مشاهده محصولات
            </Link>
          </div>
        ) : (
          <div className='grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
            {isAuthenticated
              ? // Authenticated users: show full wishlist items
                wishlistItems.map((item) => (
                  <div
                    key={item.productId}
                    className='rounded-lg overflow-hidden transition-shadow duration-300 hover:shadow-xl relative'
                    style={{
                      backgroundColor: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                    }}
                  >
                    <button
                      onClick={() => handleRemove(item.productId)}
                      className='absolute top-3 right-3 z-10 p-2 rounded-full transition-all duration-200 hover:scale-110'
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                      aria-label='حذف از علاقه‌مندی‌ها'
                    >
                      <Trash2 size={18} className='text-red-500' />
                    </button>

                    <Link to={`/products/${slugify(item.product.title)}`} state={{ productId: item.productId }}>
                      <div className='aspect-[3/4] overflow-hidden'>
                        <img
                          src={getBestImageUrl(item.product.images[0], true)}
                          alt={getImageAlt(item.product.images[0]?.alt, item.product.title, 0)}
                          title={item.product.images[0]?.title || getImageAlt(item.product.images[0]?.alt, item.product.title, 0)}
                          className='w-full h-full object-contain'
                          loading='lazy'
                        />
                      </div>

                      <div className='p-4'>
                        <h3
                          className='text-lg font-semibold mb-2 line-clamp-2'
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {makeNumbersFarsi(item.product.title)}
                        </h3>

                        {item.product.category && (
                          <p
                            className='text-sm mb-2'
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            {item.product.category.name}
                          </p>
                        )}

                        <div className='flex items-center gap-2'>
                          {item.product.compareAtPrice && (
                            <span
                              className='text-sm line-through'
                              style={{ color: 'var(--text-secondary)' }}
                            >
                              <Price
                                value={item.product.compareAtPrice}
                                currencyCode='IRR'
                                alternativeCurrencyCode={true}
                              />
                            </span>
                          )}
                          <span
                            className='text-lg font-bold'
                            style={{ color: 'var(--text-primary)' }}
                          >
                            <Price
                              value={item.product.price}
                              currencyCode='IRR'
                              alternativeCurrencyCode={true}
                            />
                          </span>
                        </div>

                        {!item.product.isAvailableForSale && (
                          <p className='text-sm mt-2 text-red-500'>
                            موجود نیست
                          </p>
                        )}
                      </div>
                    </Link>
                  </div>
                ))
              : // Guest users: show products fetched by IDs
                guestProducts.map((product) => (
                  <div
                    key={product.id}
                    className='rounded-lg overflow-hidden transition-shadow duration-300 hover:shadow-xl relative'
                    style={{
                      backgroundColor: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                    }}
                  >
                    <button
                      onClick={() => handleRemove(product.id)}
                      className='absolute top-3 right-3 z-10 p-2 rounded-full transition-all duration-200 hover:scale-110'
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                      aria-label='حذف از علاقه‌مندی‌ها'
                    >
                      <Trash2 size={18} className='text-red-500' />
                    </button>

                    <Link to={`/products/${slugify(product.title)}`} state={{ productId: product.id }}>
                      <div className='aspect-[3/4] overflow-hidden'>
                        <img
                          src={getBestImageUrl(product.images[0], true)}
                          alt={getImageAlt(product.images[0]?.alt, product.title, 0)}
                          title={product.images[0]?.title || getImageAlt(product.images[0]?.alt, product.title, 0)}
                          className='w-full h-full object-contain'
                          loading='lazy'
                        />
                      </div>

                      <div className='p-4'>
                        <h3
                          className='text-lg font-semibold mb-2 line-clamp-2'
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {makeNumbersFarsi(product.title)}
                        </h3>

                        {product.attributes?.author && (
                          <p
                            className='text-sm mb-2'
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            {product.attributes.author}
                          </p>
                        )}

                        <div className='flex items-center gap-2'>
                          {product.compareAtPrice && (
                            <span
                              className='text-sm line-through'
                              style={{ color: 'var(--text-secondary)' }}
                            >
                              <Price
                                value={product.compareAtPrice}
                                currencyCode='IRR'
                                alternativeCurrencyCode={true}
                              />
                            </span>
                          )}
                          <span
                            className='text-lg font-bold'
                            style={{ color: 'var(--text-primary)' }}
                          >
                            <Price
                              value={product.price}
                              currencyCode='IRR'
                              alternativeCurrencyCode={true}
                            />
                          </span>
                        </div>

                        {!product.isAvailableForSale && (
                          <p className='text-sm mt-2 text-red-500'>موجود نیست</p>
                        )}
                      </div>
                    </Link>
                  </div>
                ))}
          </div>
        )}
      </div>
    </div>
  )
}

